// components/EditViewJobPostModal.js
import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import moment from "moment";
import { useForm, Controller } from "react-hook-form";
import Select from "react-select";
import { 
  X, Briefcase, MapPin, Calendar, DollarSign, BookOpen, Users, FileText, 
  Save, Info, Building, GraduationCap, Clock 
} from "lucide-react";
import { ExclamationCircleIcon } from "@heroicons/react/24/outline";

const typeOfListingOptions = [
    { value: "internal", label: "Internal" },
    { value: "external", label: "External" },
    { value: "both", label: "Both" },
];

const experienceLevelOptions = [
    { value: "entry", label: "Entry Level" },
    { value: "mid", label: "Mid Level" },
    { value: "senior", label: "Senior Level" },
    { value: "executive", label: "Executive Level" },
];

const educationLevelOptions = [
    { value: "high-school", label: "High School" },
    { value: "certificate", label: "Certificate" },
    { value: "diploma", label: "Diploma" },
    { value: "bachelor", label: "Bachelor's Degree" },
    { value: "master", label: "Master's Degree" },
    { value: "phd", label: "PhD" },
];

export default function EditViewJobPostModal({ isOpen, onClose, jobPost, onUpdate, isEditing }) {
    const { register, handleSubmit, reset, setValue, control, formState: { errors }, watch } = useForm();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [selectedTypeOfListing, setSelectedTypeOfListing] = useState(null);
    const [selectedExperienceLevel, setSelectedExperienceLevel] = useState(null);
    const [selectedEducationLevel, setSelectedEducationLevel] = useState(null);
    
    const modalRef = useRef(null);

    // Watch description fields for length
    const jobDescription = watch("jobDescription") || "";
    const requirements = watch("requirements") || "";
    const responsibilities = watch("responsibilities") || "";
    const howToApply = watch("howToApply") || "";

    useEffect(() => {
        if (jobPost) {
            const formData = {
                ...jobPost,
                // Format dates for the input fields
                deadline: jobPost.deadline ? moment(jobPost.deadline).format("YYYY-MM-DD") : "",
                date: jobPost.date ? moment(jobPost.date).format("YYYY-MM-DD") : "",
            };
            
            reset(formData);
            
            // Set select options
            const typeOfListing = typeOfListingOptions.find(opt => opt.value === (jobPost.typeOfListing || "internal"));
            setSelectedTypeOfListing(typeOfListing || typeOfListingOptions[0]);
            
            const experienceLevel = experienceLevelOptions.find(opt => opt.value === (jobPost.experienceLevel || "entry"));
            setSelectedExperienceLevel(experienceLevel || experienceLevelOptions[0]);
            
            const educationLevel = educationLevelOptions.find(opt => opt.value === (jobPost.educationLevel || "high-school"));
            setSelectedEducationLevel(educationLevel || educationLevelOptions[0]);
        }
    }, [jobPost, reset]);

    useEffect(() => {
        setValue("typeOfListing", selectedTypeOfListing?.value);
    }, [selectedTypeOfListing, setValue]);

    useEffect(() => {
        setValue("experienceLevel", selectedExperienceLevel?.value);
    }, [selectedExperienceLevel, setValue]);

    useEffect(() => {
        setValue("educationLevel", selectedEducationLevel?.value);
    }, [selectedEducationLevel, setValue]);

    const onSubmit = async (data) => {
        if (!isEditing) return; // Prevent updating if not in edit mode
        
        setLoading(true);
        setError("");
        setSuccessMessage("");
        
        try {
            const response = await fetch(
                `"https://ims.holdthechild.net"/api/job-posts/${data._id}`,
                {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(data),
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to update job post.");
            }

            const updatedJob = await response.json();
            setSuccessMessage("Job post updated successfully!");
            toast.success("Job post updated successfully!");
            onUpdate(updatedJob.data); // Update the state in the parent component
            
            // Close modal after successful submission
            setTimeout(() => {
                onClose();
                setSuccessMessage("");
            }, 1500);
        } catch (error) {
            console.error("Job update failed:", error);
            const errorMessage = error.message || "Something went wrong";
            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    // Keyboard navigation
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === "Escape") {
                onClose();
            }
        };
        document.addEventListener("keydown", handleEscape);
        return () => document.removeEventListener("keydown", handleEscape);
    }, [onClose]);

    // Click outside to close
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (modalRef.current && !modalRef.current.contains(e.target)) {
                onClose();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [onClose]);

    if (!isOpen || !jobPost) return null;

    return (
        <>
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                <div
                    ref={modalRef}
                    className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[95vh] flex flex-col overflow-hidden transform transition-all duration-300 ease-out"
                >
                    {/* Enhanced Header with Orange Background */}
                    <div className="flex-shrink-0 bg-gradient-to-r from-orange-200 to-orange-200 border-b border-orange-100">
                        <div className="flex items-center justify-between px-8 py-6">
                            <div className="flex items-center space-x-4">
                                <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl shadow-sm">
                                    <Briefcase className="w-7 h-7 text-white" />
                                </div>
                                <div>
                                    <h2 className="text-2xl font-bold text-black">
                                        {isEditing ? "Edit Job Post" : "Job Post Details"}
                                    </h2>
                                    <p className="text-lg text-black-100 mt-1">
                                        Recruitment Management
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-2 rounded-xl transition-all duration-200 text-orange-200 hover:bg-white/20 hover:text-white hover:shadow-sm"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>
                    </div>

                    {/* Enhanced Body */}
                    <div className="flex-grow px-8 py-6 overflow-y-auto">
                        {error && (
                            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
                                <div className="flex items-start space-x-3">
                                    <ExclamationCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                                    <div className="text-sm text-red-700">
                                        <p className="font-medium">Error</p>
                                        <p className="mt-1">{error}</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {successMessage && (
                            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl">
                                <div className="flex items-start space-x-3">
                                    <div className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5">✓</div>
                                    <div className="text-sm text-green-700">
                                        <p className="font-medium">Success</p>
                                        <p className="mt-1">{successMessage}</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        <form onSubmit={handleSubmit(onSubmit)} id="job-form" className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800 mb-3">
                                        <span>Vacancy Title</span>
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                            Required
                                        </span>
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Briefcase className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input
                                            {...register("vacancy", { 
                                                required: "Vacancy title is required",
                                                minLength: { value: 3, message: "Vacancy title must be at least 3 characters" }
                                            })}
                                            type="text"
                                            disabled={!isEditing}
                                            className={`w-full pl-10 pr-3 py-3 text-sm rounded-xl border ${errors.vacancy ? 'border-red-300 bg-red-50' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed`}
                                            placeholder="Enter vacancy title"
                                        />
                                    </div>
                                    {errors.vacancy && (
                                        <p className="mt-2 text-sm text-red-600 flex items-center space-x-1">
                                            <ExclamationCircleIcon className="w-4 h-4" />
                                            <span>{errors.vacancy.message}</span>
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800 mb-3">
                                        <span>Department</span>
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                            Required
                                        </span>
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Building className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input
                                            {...register("department", { 
                                                required: "Department is required"
                                            })}
                                            type="text"
                                            disabled={!isEditing}
                                            className={`w-full pl-10 pr-3 py-3 text-sm rounded-xl border ${errors.department ? 'border-red-300 bg-red-50' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed`}
                                            placeholder="Enter department"
                                        />
                                    </div>
                                    {errors.department && (
                                        <p className="mt-2 text-sm text-red-600 flex items-center space-x-1">
                                            <ExclamationCircleIcon className="w-4 h-4" />
                                            <span>{errors.department.message}</span>
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800 mb-3">
                                        <span>Location</span>
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                            Required
                                        </span>
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <MapPin className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input
                                            {...register("location", { 
                                                required: "Location is required"
                                            })}
                                            type="text"
                                            disabled={!isEditing}
                                            className={`w-full pl-10 pr-3 py-3 text-sm rounded-xl border ${errors.location ? 'border-red-300 bg-red-50' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed`}
                                            placeholder="Enter location"
                                        />
                                    </div>
                                    {errors.location && (
                                        <p className="mt-2 text-sm text-red-600 flex items-center space-x-1">
                                            <ExclamationCircleIcon className="w-4 h-4" />
                                            <span>{errors.location.message}</span>
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800 mb-3">
                                        <span>Type of Listing</span>
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                            Required
                                        </span>
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Users className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <Select
                                            value={selectedTypeOfListing}
                                            onChange={setSelectedTypeOfListing}
                                            options={typeOfListingOptions}
                                            isDisabled={!isEditing}
                                            className="w-full rounded-xl"
                                            placeholder="Select type of listing"
                                            styles={{
                                                control: (base, state) => ({
                                                    ...base,
                                                    borderRadius: '0.75rem',
                                                    minHeight: '44px',
                                                    paddingLeft: '42px',
                                                    borderColor: state.isFocused || errors.typeOfListing ? '#F97316' : '#D1D5DB',
                                                    boxShadow: state.isFocused ? '0 0 0 1px #F97316' : 'none',
                                                    '&:hover': {
                                                        borderColor: '#F97316'
                                                    },
                                                    backgroundColor: !isEditing ? '#F3F4F6' : 'white'
                                                }),
                                                placeholder: (base) => ({
                                                    ...base,
                                                    color: '#9CA3AF'
                                                }),
                                                singleValue: (base) => ({
                                                    ...base,
                                                    color: '#1F2937'
                                                }),
                                                option: (base, state) => ({
                                                    ...base,
                                                    backgroundColor: state.isSelected ? '#F97316' : state.isFocused ? '#FFEDD5' : 'white',
                                                    color: state.isSelected ? 'white' : '#1F2937',
                                                    '&:hover': {
                                                        backgroundColor: '#FFEDD5',
                                                        color: '#1F2937'
                                                    }
                                                }),
                                                dropdownIndicator: (base) => ({
                                                    ...base,
                                                    color: '#6B7280',
                                                    '&:hover': {
                                                        color: '#F97316'
                                                    }
                                                }),
                                                indicatorSeparator: (base) => ({
                                                    ...base,
                                                    backgroundColor: '#D1D5DB'
                                                }),
                                                menu: (base) => ({
                                                    ...base,
                                                    borderRadius: '0.75rem',
                                                    border: '1px solid #E5E7EB'
                                                })
                                            }}
                                        />
                                    </div>
                                    <input type="hidden" {...register("typeOfListing")} value={selectedTypeOfListing?.value} />
                                    {errors.typeOfListing && (
                                        <p className="mt-2 text-sm text-red-600 flex items-center space-x-1">
                                            <ExclamationCircleIcon className="w-4 h-4" />
                                            <span>{errors.typeOfListing.message}</span>
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800 mb-3">
                                        <span>Experience Level</span>
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                            Required
                                        </span>
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Clock className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <Select
                                            value={selectedExperienceLevel}
                                            onChange={setSelectedExperienceLevel}
                                            options={experienceLevelOptions}
                                            isDisabled={!isEditing}
                                            className="w-full rounded-xl"
                                            placeholder="Select experience level"
                                            styles={{
                                                control: (base, state) => ({
                                                    ...base,
                                                    borderRadius: '0.75rem',
                                                    minHeight: '44px',
                                                    paddingLeft: '42px',
                                                    borderColor: state.isFocused || errors.experienceLevel ? '#F97316' : '#D1D5DB',
                                                    boxShadow: state.isFocused ? '0 0 0 1px #F97316' : 'none',
                                                    '&:hover': {
                                                        borderColor: '#F97316'
                                                    },
                                                    backgroundColor: !isEditing ? '#F3F4F6' : 'white'
                                                }),
                                                placeholder: (base) => ({
                                                    ...base,
                                                    color: '#9CA3AF'
                                                }),
                                                singleValue: (base) => ({
                                                    ...base,
                                                    color: '#1F2937'
                                                }),
                                                option: (base, state) => ({
                                                    ...base,
                                                    backgroundColor: state.isSelected ? '#F97316' : state.isFocused ? '#FFEDD5' : 'white',
                                                    color: state.isSelected ? 'white' : '#1F2937',
                                                    '&:hover': {
                                                        backgroundColor: '#FFEDD5',
                                                        color: '#1F2937'
                                                    }
                                                }),
                                                dropdownIndicator: (base) => ({
                                                    ...base,
                                                    color: '#6B7280',
                                                    '&:hover': {
                                                        color: '#F97316'
                                                    }
                                                }),
                                                indicatorSeparator: (base) => ({
                                                    ...base,
                                                    backgroundColor: '#D1D5DB'
                                                }),
                                                menu: (base) => ({
                                                    ...base,
                                                    borderRadius: '0.75rem',
                                                    border: '1px solid #E5E7EB'
                                                })
                                            }}
                                        />
                                    </div>
                                    <input type="hidden" {...register("experienceLevel")} value={selectedExperienceLevel?.value} />
                                    {errors.experienceLevel && (
                                        <p className="mt-2 text-sm text-red-600 flex items-center space-x-1">
                                            <ExclamationCircleIcon className="w-4 h-4" />
                                            <span>{errors.experienceLevel.message}</span>
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800 mb-3">
                                        <span>Education Level</span>
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                            Required
                                        </span>
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <GraduationCap className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <Select
                                            value={selectedEducationLevel}
                                            onChange={setSelectedEducationLevel}
                                            options={educationLevelOptions}
                                            isDisabled={!isEditing}
                                            className="w-full rounded-xl"
                                            placeholder="Select education level"
                                            styles={{
                                                control: (base, state) => ({
                                                    ...base,
                                                    borderRadius: '0.75rem',
                                                    minHeight: '44px',
                                                    paddingLeft: '42px',
                                                    borderColor: state.isFocused || errors.educationLevel ? '#F97316' : '#D1D5DB',
                                                    boxShadow: state.isFocused ? '0 0 0 1px #F97316' : 'none',
                                                    '&:hover': {
                                                        borderColor: '#F97316'
                                                    },
                                                    backgroundColor: !isEditing ? '#F3F4F6' : 'white'
                                                }),
                                                placeholder: (base) => ({
                                                    ...base,
                                                    color: '#9CA3AF'
                                                }),
                                                singleValue: (base) => ({
                                                    ...base,
                                                    color: '#1F2937'
                                                }),
                                                option: (base, state) => ({
                                                    ...base,
                                                    backgroundColor: state.isSelected ? '#F97316' : state.isFocused ? '#FFEDD5' : 'white',
                                                    color: state.isSelected ? 'white' : '#1F2937',
                                                    '&:hover': {
                                                        backgroundColor: '#FFEDD5',
                                                        color: '#1F2937'
                                                    }
                                                }),
                                                dropdownIndicator: (base) => ({
                                                    ...base,
                                                    color: '#6B7280',
                                                    '&:hover': {
                                                        color: '#F97316'
                                                    }
                                                }),
                                                indicatorSeparator: (base) => ({
                                                    ...base,
                                                    backgroundColor: '#D1D5DB'
                                                }),
                                                menu: (base) => ({
                                                    ...base,
                                                    borderRadius: '0.75rem',
                                                    border: '1px solid #E5E7EB'
                                                })
                                            }}
                                        />
                                    </div>
                                    <input type="hidden" {...register("educationLevel")} value={selectedEducationLevel?.value} />
                                    {errors.educationLevel && (
                                        <p className="mt-2 text-sm text-red-600 flex items-center space-x-1">
                                            <ExclamationCircleIcon className="w-4 h-4" />
                                            <span>{errors.educationLevel.message}</span>
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800 mb-3">
                                        <span>Salary Range</span>
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                            Required
                                        </span>
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <DollarSign className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input
                                            {...register("salaryRange", { 
                                                required: "Salary range is required"
                                            })}
                                            type="text"
                                            disabled={!isEditing}
                                            className={`w-full pl-10 pr-3 py-3 text-sm rounded-xl border ${errors.salaryRange ? 'border-red-300 bg-red-50' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed`}
                                            placeholder="Enter salary range"
                                        />
                                    </div>
                                    {errors.salaryRange && (
                                        <p className="mt-2 text-sm text-red-600 flex items-center space-x-1">
                                            <ExclamationCircleIcon className="w-4 h-4" />
                                            <span>{errors.salaryRange.message}</span>
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800 mb-3">
                                        <span>Age Range</span>
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                            Optional
                                        </span>
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Users className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input
                                            {...register("ageRange")}
                                            type="text"
                                            disabled={!isEditing}
                                            className={`w-full pl-10 pr-3 py-3 text-sm rounded-xl border ${errors.ageRange ? 'border-red-300 bg-red-50' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed`}
                                            placeholder="Enter age range"
                                        />
                                    </div>
                                    {errors.ageRange && (
                                        <p className="mt-2 text-sm text-red-600 flex items-center space-x-1">
                                            <ExclamationCircleIcon className="w-4 h-4" />
                                            <span>{errors.ageRange.message}</span>
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800 mb-3">
                                        <span>Deadline</span>
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                            Required
                                        </span>
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Calendar className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input
                                            {...register("deadline", { 
                                                required: "Deadline is required"
                                            })}
                                            type="date"
                                            disabled={!isEditing}
                                            className={`w-full pl-10 pr-3 py-3 text-sm rounded-xl border ${errors.deadline ? 'border-red-300 bg-red-50' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed`}
                                        />
                                    </div>
                                    {errors.deadline && (
                                        <p className="mt-2 text-sm text-red-600 flex items-center space-x-1">
                                            <ExclamationCircleIcon className="w-4 h-4" />
                                            <span>{errors.deadline.message}</span>
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800 mb-3">
                                        <span>Posting Date</span>
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                            Required
                                        </span>
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Calendar className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input
                                            {...register("date", { 
                                                required: "Posting date is required"
                                            })}
                                            type="date"
                                            disabled={!isEditing}
                                            className={`w-full pl-10 pr-3 py-3 text-sm rounded-xl border ${errors.date ? 'border-red-300 bg-red-50' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed`}
                                        />
                                    </div>
                                    {errors.date && (
                                        <p className="mt-2 text-sm text-red-600 flex items-center space-x-1">
                                            <ExclamationCircleIcon className="w-4 h-4" />
                                            <span>{errors.date.message}</span>
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div>
                                <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800 mb-3">
                                    <span>Requirements</span>
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                        Required
                                    </span>
                                </label>
                                <div className="relative">
                                    <div className="absolute top-3 left-3 flex items-start pointer-events-none">
                                        <FileText className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <textarea
                                        {...register("requirements", { 
                                            required: "Requirements are required",
                                            minLength: { value: 10, message: "Requirements must be at least 10 characters" }
                                        })}
                                        rows={3}
                                        disabled={!isEditing}
                                        className={`w-full pl-10 pr-3 py-3 text-sm rounded-xl border ${errors.requirements ? 'border-red-300 bg-red-50' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 resize-none disabled:bg-gray-100 disabled:cursor-not-allowed`}
                                        placeholder="List the requirements for this position..."
                                    />
                                </div>
                                <div className="flex justify-between items-center mt-2">
                                    <p className="text-xs text-gray-500">
                                        {requirements.length > 0 ? 'List the required qualifications and skills' : 'Provide detailed requirements'}
                                    </p>
                                    <span className="text-xs text-gray-400">
                                        {requirements.length}/500
                                    </span>
                                </div>
                                {errors.requirements && (
                                    <p className="mt-2 text-sm text-red-600 flex items-center space-x-1">
                                        <ExclamationCircleIcon className="w-4 h-4" />
                                        <span>{errors.requirements.message}</span>
                                    </p>
                                )}
                            </div>

                            <div>
                                <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800 mb-3">
                                    <span>Job Description</span>
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                        Required
                                    </span>
                                </label>
                                <div className="relative">
                                    <div className="absolute top-3 left-3 flex items-start pointer-events-none">
                                        <FileText className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <textarea
                                        {...register("jobDescription", { 
                                            required: "Job description is required",
                                            minLength: { value: 20, message: "Job description must be at least 20 characters" }
                                        })}
                                        rows={4}
                                        disabled={!isEditing}
                                        className={`w-full pl-10 pr-3 py-3 text-sm rounded-xl border ${errors.jobDescription ? 'border-red-300 bg-red-50' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 resize-none disabled:bg-gray-100 disabled:cursor-not-allowed`}
                                        placeholder="Describe the job role and responsibilities..."
                                    />
                                </div>
                                <div className="flex justify-between items-center mt-2">
                                    <p className="text-xs text-gray-500">
                                        {jobDescription.length > 0 ? 'Provide a detailed job description' : 'Describe the role in detail'}
                                    </p>
                                    <span className="text-xs text-gray-400">
                                        {jobDescription.length}/1000
                                    </span>
                                </div>
                                {errors.jobDescription && (
                                    <p className="mt-2 text-sm text-red-600 flex items-center space-x-1">
                                        <ExclamationCircleIcon className="w-4 h-4" />
                                        <span>{errors.jobDescription.message}</span>
                                    </p>
                                )}
                            </div>

                            <div>
                                <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800 mb-3">
                                    <span>Responsibilities</span>
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                        Required
                                    </span>
                                </label>
                                <div className="relative">
                                    <div className="absolute top-3 left-3 flex items-start pointer-events-none">
                                        <FileText className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <textarea
                                        {...register("responsibilities", { 
                                            required: "Responsibilities are required",
                                            minLength: { value: 10, message: "Responsibilities must be at least 10 characters" }
                                        })}
                                        rows={3}
                                        disabled={!isEditing}
                                        className={`w-full pl-10 pr-3 py-3 text-sm rounded-xl border ${errors.responsibilities ? 'border-red-300 bg-red-50' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 resize-none disabled:bg-gray-100 disabled:cursor-not-allowed`}
                                        placeholder="List the key responsibilities..."
                                    />
                                </div>
                                <div className="flex justify-between items-center mt-2">
                                    <p className="text-xs text-gray-500">
                                        {responsibilities.length > 0 ? 'List the key responsibilities' : 'Provide detailed responsibilities'}
                                    </p>
                                    <span className="text-xs text-gray-400">
                                        {responsibilities.length}/500
                                    </span>
                                </div>
                                {errors.responsibilities && (
                                    <p className="mt-2 text-sm text-red-600 flex items-center space-x-1">
                                        <ExclamationCircleIcon className="w-4 h-4" />
                                        <span>{errors.responsibilities.message}</span>
                                    </p>
                                )}
                            </div>

                            <div>
                                <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800 mb-3">
                                    <span>How to Apply</span>
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                        Required
                                    </span>
                                </label>
                                <div className="relative">
                                    <div className="absolute top-3 left-3 flex items-start pointer-events-none">
                                        <FileText className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <textarea
                                        {...register("howToApply", { 
                                            required: "Application instructions are required",
                                            minLength: { value: 10, message: "Application instructions must be at least 10 characters" }
                                        })}
                                        rows={3}
                                        disabled={!isEditing}
                                        className={`w-full pl-10 pr-3 py-3 text-sm rounded-xl border ${errors.howToApply ? 'border-red-300 bg-red-50' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 resize-none disabled:bg-gray-100 disabled:cursor-not-allowed`}
                                        placeholder="Provide instructions on how to apply..."
                                    />
                                </div>
                                <div className="flex justify-between items-center mt-2">
                                    <p className="text-xs text-gray-500">
                                        {howToApply.length > 0 ? 'Provide application instructions' : 'Explain the application process'}
                                    </p>
                                    <span className="text-xs text-gray-400">
                                        {howToApply.length}/500
                                    </span>
                                </div>
                                {errors.howToApply && (
                                    <p className="mt-2 text-sm text-red-600 flex items-center space-x-1">
                                        <ExclamationCircleIcon className="w-4 h-4" />
                                        <span>{errors.howToApply.message}</span>
                                    </p>
                                )}
                            </div>
                        </form>
                    </div>

                    {/* Enhanced Footer */}
                    <div className="flex-shrink-0 bg-gray-50 px-8 py-6 border-t border-gray-200">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                                <div className="flex items-center space-x-2 text-sm text-gray-600">
                                    <Info className="w-4 h-4 text-orange-500" />
                                    <span>All fields marked as required must be completed</span>
                                </div>
                            </div>

                            <div className="flex items-center space-x-4">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="px-6 py-3 rounded-xl font-semibold transition-all duration-200 text-gray-700 hover:bg-gray-200"
                                >
                                    Close
                                </button>

                                {isEditing && (
                                    <button
                                        type="submit"
                                        form="job-form"
                                        disabled={loading}
                                        className={`relative px-8 py-3 rounded-xl font-semibold text-white transition-all duration-200 transform ${
                                            loading
                                                ? 'bg-orange-400 cursor-not-allowed'
                                                : 'bg-gradient-to-r from-orange-400 to-orange-500 hover:from-orange-500 hover:to-orange-600 hover:scale-105 shadow-lg hover:shadow-xl'
                                        }`}
                                    >
                                        {loading ? (
                                            <div className="flex items-center space-x-3">
                                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                <span>Updating...</span>
                                            </div>
                                        ) : (
                                            <div className="flex items-center space-x-2">
                                                <Save className="w-5 h-5" />
                                                <span>Update Job Post</span>
                                            </div>
                                        )}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}